import { ConfirmationDialog, MessagePanel } from '@dittolive/anvil'
import React, { useState } from 'react'

import type { PortalApiKey } from '../../../types'

type Props = {
  /** The API key to delete. Also used to
   * indicate whether the modal is open or closed. */
  apiKey: PortalApiKey | undefined
  /** Callback for closing the modal */
  onClose: () => void
  /** Callback for deleting the API key */
  onDelete: (apiKeyId: string) => Promise<void>
}

export default function ApiKeyDeletionModal({
  apiKey,
  onClose,
  onDelete,
}: Props) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [deletionError, setDeletionError] = useState<string>()

  const handleDeleteApiKey = async () => {
    if (!apiKey?.id) return

    setDeletionError(undefined)
    setIsDeleting(true)

    try {
      await onDelete(apiKey.id)
      onClose()
    } catch (err) {
      if (err instanceof Error) {
        setDeletionError(err.message)
      } else {
        setDeletionError('An error occurred while deleting the API key')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <ConfirmationDialog
      size="md"
      open={!!apiKey && apiKey.viewerCanDelete}
      onCancel={onClose}
      onConfirm={handleDeleteApiKey}
      title={
        apiKey?.description
          ? `Delete API key "${apiKey.description}"?`
          : 'Delete API key?'
      }
      confirmButtonText="Delete API key"
      cancelButtonDisabled={isDeleting}
      confirmButtonDisabled={isDeleting || !apiKey?.viewerCanDelete}
      variant="warning"
      description={
        <>
          Are you sure you want to delete this API key? You will need to{' '}
          <span className="text-foreground-normal font-medium">
            ensure all applications using this API key are updated
          </span>{' '}
          with a new API key. <br />
          <br />
          This action{' '}
          <span className="text-foreground-normal font-medium">
            cannot be undone
          </span>
          .
        </>
      }
    >
      {!!deletionError && (
        <MessagePanel
          message={deletionError}
          variant="danger"
          className="my-2"
        />
      )}
    </ConfirmationDialog>
  )
}
